"use client";

import { useMemo } from "react";

import {
  calculateCartTotals,
  formatPrice,
  qualifiesForFreeShipping,
  remainingForFreeShipping,
  type CartCalculationConfig,
  type CartCalculationResult,
} from "@/lib/utils/cart-calculations";
import { useCartStore } from "@/store/cart";

export interface UseCartCalculationsOptions extends CartCalculationConfig {
  currencyIcon?: string;
}

export interface UseCartCalculationsReturn extends CartCalculationResult {
  formattedSubtotal: string;
  formattedDiscount: string;
  formattedTax: string;
  formattedShipping: string;
  formattedCouponDiscount: string;
  formattedTotal: string;
  hasFreeShipping: boolean;
  remainingForFreeShipping: number;
  formattedRemainingForFreeShipping: string;
}

/**
 * Hook for cart calculations (tax, shipping, total)
 * Use this on cart page only
 */
export function useCartCalculations(
  options: UseCartCalculationsOptions = {}
): UseCartCalculationsReturn {
  const items = useCartStore((state) => state.items);
  const {
    taxRate = 0,
    shippingFee = 0,
    freeShippingThreshold = 0,
    couponDiscount = 0,
    couponType = "fixed",
    currencyIcon = "£",
  } = options;

  const calculations = useMemo(() => {
    const totals = calculateCartTotals(items, {
      taxRate,
      shippingFee,
      freeShippingThreshold,
      couponDiscount,
      couponType,
    });

    const hasFreeShipping = qualifiesForFreeShipping(
      totals.subtotal,
      freeShippingThreshold
    );

    const remaining = remainingForFreeShipping(
      totals.subtotal,
      freeShippingThreshold
    );

    return {
      ...totals,
      formattedSubtotal: formatPrice(totals.subtotal, currencyIcon),
      formattedDiscount: formatPrice(totals.discount, currencyIcon),
      formattedTax: formatPrice(totals.tax, currencyIcon),
      formattedShipping: formatPrice(totals.shipping, currencyIcon),
      formattedCouponDiscount: formatPrice(totals.couponDiscount, currencyIcon),
      formattedTotal: formatPrice(totals.total, currencyIcon),
      hasFreeShipping,
      remainingForFreeShipping: remaining,
      formattedRemainingForFreeShipping: formatPrice(remaining, currencyIcon),
    };
  }, [
    items,
    taxRate,
    shippingFee,
    freeShippingThreshold,
    couponDiscount,
    couponType,
    currencyIcon,
  ]);

  return calculations;
}
